import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { formatMoney }  from '../../utils/formatting.js';
import { CARGO_TYPES }  from '../../config/cargoTypes.js';

const LEVEL_LABELS = {
  1: 'L1',
  2: 'L2',
  3: 'L3'
};

const LEVEL_CLASSES = {
  1: 'wh-level-1',
  2: 'wh-level-2',
  3: 'wh-level-3'
};

export class WarehousesPanel {
  constructor(uiManager) {
    this.ui = uiManager;
  }

  render() {
    const container = document.querySelector('.panel-content');
    const warehouses = [...GameState.warehouses.values()];

    // Calculate aggregate stats
    const totalCapacity = warehouses.reduce((sum, wh) => sum + wh.capacity, 0);
    const totalUsed = warehouses.reduce((sum, wh) => sum + wh.cargoUsed, 0);
    let totalValue = 0;
    for (const wh of warehouses) {
      for (const [cargoId, data] of Object.entries(wh.cargoAverages)) {
        totalValue += (data.totalQty || 0) * (data.avgPrice || 0);
      }
    }

    // Header bar
    let html = `
      <div class="routes-header">
        <div class="routes-header-actions">
          <h4 style="margin: 0; color: var(--text-primary);">Warehouses</h4>
        </div>
        <div class="routes-header-stats">
          <span>${warehouses.length} warehouse${warehouses.length !== 1 ? 's' : ''}</span>
          <span>Total: ${totalUsed}/${totalCapacity} TEU</span>
          <span>Est. value: ${formatMoney(totalValue)}</span>
        </div>
      </div>`;

    // Warehouse cards
    if (warehouses.length === 0) {
      html += '<p class="panel-empty">No warehouses built. Build warehouses from port Services tab.</p>';
    } else {
      html += '<div class="routes-list">';
      for (const wh of warehouses) {
        const port = GameState.getPort(wh.portId);
        const portName = port ? port.name : 'Unknown';
        const country = port ? port.country : '';
        const usagePercent = wh.capacity > 0 ? (wh.cargoUsed / wh.capacity) * 100 : 0;

        // Calculate warehouse value
        let whValue = 0;
        for (const [cargoId, data] of Object.entries(wh.cargoAverages)) {
          whValue += (data.totalQty || 0) * (data.avgPrice || 0);
        }

        // Build cargo summary (compact list)
        const cargoSummary = [];
        const cargoMap = new Map();
        for (const cargo of wh.cargo) {
          const existing = cargoMap.get(cargo.type);
          if (existing) {
            existing.quantity += cargo.quantity;
          } else {
            cargoMap.set(cargo.type, { quantity: cargo.quantity });
          }
        }
        for (const [typeId, data] of cargoMap) {
          const cargoDef = CARGO_TYPES[typeId];
          const name = cargoDef ? cargoDef.name : typeId;
          cargoSummary.push(`${data.quantity} ${name}`);
        }

        const cargoDisplay = cargoSummary.length > 0
          ? cargoSummary.join(', ')
          : '<span style="color: var(--text-muted); font-style: italic;">Empty</span>';

        const levelLabel = LEVEL_LABELS[wh.level] || 'L1';
        const levelClass = LEVEL_CLASSES[wh.level] || 'wh-level-1';

        const maxShipText = wh.maxShipSize === Infinity ? 'Unlimited' : `${wh.maxShipSize} TEU`;

        html += `<div class="wh-card">
          <div class="wh-card-header">
            <div>
              <strong>${portName}</strong>
              ${country ? `<span style="color: var(--text-muted); font-size: 12px; margin-left: 4px;">${country}</span>` : ''}
            </div>
            <span class="tier-badge ${levelClass}">${levelLabel}</span>
          </div>
          <div class="wh-card-body">
            <div class="wh-usage-section">
              <div class="wh-usage-text">${wh.cargoUsed}/${wh.capacity} TEU</div>
              <div class="wh-usage-bar-container">
                <div class="wh-usage-bar" style="width: ${usagePercent}%"></div>
              </div>
            </div>
            <div class="wh-card-info">
              <span><strong>Max ship:</strong> ${maxShipText}</span>
              <span><strong>Value:</strong> ${formatMoney(whValue)}</span>
            </div>
            <div class="wh-card-cargo">
              <strong>Cargo:</strong> ${cargoDisplay}
            </div>
          </div>
          <div class="wh-card-actions">
            <button class="btn btn-sm btn-primary" data-manage-warehouse="${wh.id}">Manage</button>
          </div>
        </div>`;
      }
      html += '</div>';
    }

    container.innerHTML = html;

    // Bind events
    container.querySelectorAll('[data-manage-warehouse]').forEach(btn => {
      btn.addEventListener('click', () => {
        const whId = btn.dataset.manageWarehouse;
        const wh = GameState.warehouses.get(whId);
        if (wh) {
          const port = GameState.getPort(wh.portId);
          if (port) {
            EventBus.emit('ui:warehouseOpen', { port });
          }
        }
      });
    });
  }
}
